import type { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import type { OverviewCardDataProps } from '@ctw/shared/content/overview/resource-overview-card';
import { ResourceOverviewCard } from '@ctw/shared/content/overview/resource-overview-card';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { tw } from '@ctw/shared/utils/tailwind';
import { faFlask } from '@fortawesome/pro-solid-svg-icons';
import { RenderSyncedWithRecordIcon, ResourceTitleColumn } from '../resource/resource-title-column';
import { containsText, hasA1CCode } from './use-patient-diagnostic-reports-overview-data';

const TITLE = 'Select Labs';

const cleanupA1CDisplay = (A1CCodes: Array<string>, r: DiagnosticReportModel) => {
  // First check that it is a 1AC display
  if (hasA1CCode(A1CCodes, r) || containsText(r, 'a1c')) {
    if (hasA1CCode(A1CCodes, r)) {
      return r.displayName;
    }
    return 'A1c';
  }

  // If not an a1c display don't manipulate text
  return r.displayName;
};

export interface PatientDiagnosticReportsOverviewCardProps
  extends OverviewCardDataProps<DiagnosticReportModel> {
  a1cCodes: Array<string>;
}

/**
 * Presentational labs overview card. Renders the pre-merged list it is handed;
 * data fetching + merge live in `usePatientDiagnosticReportsOverviewData`.
 */
export const PatientDiagnosticReportsOverviewCard = ({
  data,
  isLoading,
  emptyState,
  footerCTA,
  onRowClick,
  a1cCodes,
}: PatientDiagnosticReportsOverviewCardProps) => {
  const { requestContext } = useCTW();

  return (
    <ResourceOverviewCard
      onRowClick={onRowClick}
      data={data}
      emptyState={emptyState}
      footerCTA={footerCTA}
      title={TITLE}
      helpText="Most recent Metabolic or CBC Panel and A1C"
      loading={isLoading}
      headerIcon={faFlask}
      testId="labs-overview"
      renderResource={(record: DiagnosticReportModel) => (
        <div>
          <ResourceTitleColumn
            title={cleanupA1CDisplay(a1cCodes, record)}
            renderIcon={RenderSyncedWithRecordIcon(record.ownedByBuilder(requestContext.builderId))}
          />
          <div className={tw`font-normal`}>Recorded: {record.dateDisplay}</div>
        </div>
      )}
    />
  );
};
